import { useRef, useState } from "react";
import { TProcessStates } from "../../../lib/types/general";
import { LabSignUpFormData, TApiAddress, TApiLab } from "../../../lib/types/labs";
import { Spinner } from "../../common/spinner";

type TNameSectionProps = {
    editable: boolean;
    userId: string | undefined;
    isSomePartBeingEdited: boolean;
    userInfo: TApiLab;
    workingState: TProcessStates;
    setUserInfo: React.Dispatch<React.SetStateAction<TApiLab>>;
    setIsSomePartBeingEdited: (value: React.SetStateAction<boolean>) => void;
    updateLabAndLocalStorage: (data: LabSignUpFormData, name?: string) => Promise<void>;
    verifyDuplicateNameAddress: (name: string, address: TApiAddress, userId: number) => Promise<boolean>;
};

export const NameSection = ({
    editable,
    isSomePartBeingEdited,
    userInfo,
    userId,
    workingState,
    setUserInfo,
    setIsSomePartBeingEdited,
    updateLabAndLocalStorage,
    verifyDuplicateNameAddress,
}: TNameSectionProps) => {
    const [isEditingName, setIsEditingName] = useState(false);
    const nameRef = useRef<any>();
    const [duplicateName, setDuplicateName] = useState(false);
    const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");
    const editableOnClickHandler = (e: any) => {
        if (!editable) return;
        if (isSomePartBeingEdited) return;
        e.preventDefault();
        e.stopPropagation();
        setIsEditingName(true);
        setIsSomePartBeingEdited(true);
    };

    const saveName = async () => {
        setCurrWorkingState("working");
        const newName = nameRef.current.value;

        if (userInfo.other.address) {
            const validateDuplicate = await verifyDuplicateNameAddress(newName, userInfo.other.address, userInfo.id);
            if (validateDuplicate) {
                setDuplicateName(true);
                setCurrWorkingState("done");
                return;
            }
        }

        await updateLabAndLocalStorage(
            {
                password: "",
                ...userInfo.other,
            },
            newName,
        );
        setUserInfo({
            ...userInfo,
            name: newName,
        });
        setDuplicateName(false);
        setCurrWorkingState("done");
        setIsEditingName(false);
        setIsSomePartBeingEdited(false);
    };

    const cancelHandler = async () => {
        nameRef.current.value = userInfo?.name;
        setDuplicateName(false);
        setIsEditingName(false);
        setIsSomePartBeingEdited(false);
    };

    return (
        <>
            {!isEditingName && (
                <>
                    {userId && (
                        <div className="text-break">
                            <h4
                                className={
                                    workingState !== "done" || currWorkingState !== "done"
                                        ? "cssLoading fw-bold employer-name mb-0"
                                        : "fw-bold employer-name mb-0"
                                }
                            >
                                {workingState === "done" ? nameRef.current?.value || userInfo?.name : <>&nbsp;</>}
                            </h4>
                            <small className="d-block text-muted mt-1">{userInfo?.business_type_name}</small>
                        </div>
                    )}
                    {editable && (
                        <div className="d-flex align-items-start justify-content-between gap-2 text-break">
                            <div
                                className="flex-grow-1"
                                role="button"
                                onClick={(e) => {
                                    if (isSomePartBeingEdited) return;
                                    editableOnClickHandler(e);
                                }}
                            >
                                <div
                                    className={
                                        "fs-4 fw-bold employer-name mb-0" + (workingState !== "done" || currWorkingState !== "done" ? " cssLoading" : "")
                                    }
                                >
                                    {workingState === "done" ? nameRef.current?.value || userInfo?.name : <>&nbsp;</>}
                                </div>
                                <small className="d-block text-muted mt-1">{userInfo?.business_type_name}</small>
                            </div>

                            {!isSomePartBeingEdited && (
                                <div
                                    className="fileTooltip rightEdit ms-auto flex-shrink-0"
                                    role="button"
                                    data-tooltip=" Edit "
                                    onClick={editableOnClickHandler}
                                >
                                    <i className="fa-solid fa-pen-to-square fa-xs p-2 shadow editIcon" data-testid="edit-icon"></i>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
            {isEditingName && (
                <div className="text-center my-3">
                    <input className="text-center mb-2 form-control form-control-lg" ref={nameRef} type="text" defaultValue={userInfo?.name} maxLength={50} />
                    {duplicateName && (
                        <div className="alert alert-danger text-start py-1 px-2 rounded-3" role="alert">
                            <p style={{ fontSize: "small" }} className="m-0">
                                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                This name is already registered at this address. Please choose another name or update your address.
                            </p>
                        </div>
                    )}
                    <div className="d-grid gap-3 mt-2 w-100 d-md-block">
                        <button className="btn btn-primary px-4" onClick={saveName}>
                            {currWorkingState === "working" ? <Spinner /> : <>Save</>}
                        </button>
                        <button className="btn btn-danger ms-md-2 px-3 " onClick={cancelHandler}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};
